import React, { useCallback, useMemo } from 'react';
import i18n from 'i18n';
import {
  CampaignDashboardColumns,
  canManageAdminBidPrice,
  useCampaignDashboardModel
} from './CampaignDashboardModel';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import styles from './campaignDashboard.module.scss';
import { InfoDisplaySection } from 'components/InfoDisplaySection/InfoDisplaySection';
import Select from 'components/common/Select/Select';
import { SearchBar } from 'components/common/SearchBar';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBalanceScaleLeft,
  faChartArea,
  faDollarSign,
  faDownload,
  faPencilAlt
} from '@fortawesome/free-solid-svg-icons';
import { CampaignDashboardRecord } from 'core/campaignDashboard/CampaignDashboard';
import PermissionChecker from 'containers/PermissionChecker/PermissionChecker';
import {
  addOnEnabled,
  hasTheseRoles,
  hasFuncs,
  isRoles,
  isPmax3Order,
  inCondition
} from 'core/permission/PermissionDSL';
import { Permission } from 'core/auth/Permission';
import { Link } from 'react-router-dom';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import moment from 'moment';
import { RoleNames } from 'core';
import { ADDONFEATURE } from 'core/agency/AddonFeature';
import { BidWeightModal } from 'components/GodManagementModal/BidWeightModal';
import { AdminBidPriceModal } from 'components/GodManagementModal/AdminBidPriceModal';

export type CampaignDashboardProps = {
  agencyId: number | null | undefined;
};

const getReportLink = (campaignDashbaordRecord: CampaignDashboardRecord) => {
  const timeFormat = 'YYYY-MM-DD HH:mm:ss';
  const from = encodeURIComponent(
    moment(campaignDashbaordRecord.startDate).startOf('day').format(timeFormat)
  );
  const to = encodeURIComponent(
    moment(campaignDashbaordRecord.endDate).endOf('day').format(timeFormat)
  );
  const dimension = 'l2ObjectId';
  return `/reports/performance?dimension=${dimension}&from=${from}&to=${to}&${dimension}=${campaignDashbaordRecord.l2ObjectId}`;
};

export const CampaignDashboard: React.FC<CampaignDashboardProps> = ({
  agencyId
}) => {
  const {
    days,
    loading,
    searched,
    timeOption,
    todaySummary,
    searchString,
    yesterdaySummary,
    columns,
    filteredList,
    adTypeOptions,
    campaignStatusOptions,
    adjustmentTagOptions,
    selectedAdTypes,
    selectedCampaignStates,
    selectedTagCodes,
    setSelectedAdTypes,
    setSelectedCampaignStates,
    setSelectedTagCodes,
    campaignToChangeBidWeight,
    campaignToChangeAdminBidPrice,
    isPmp,
    setDays,
    download,
    setTimeOption,
    setSearchString,
    onSearchButtonClick,
    showManageBidWeightModal,
    hideManageBidWeightModal,
    showManageAdminBidPriceModal,
    hideManageAdminBidPriceModal,
    onCampaignBidWeightUpdated,
    onCampaignAdminBidPriceUpdated
  } = useCampaignDashboardModel(agencyId);

  const dayOptions = useMemo(
    () =>
      [1, 7, 30, 60, 90, 180].map((day) => ({
        label: day.toString(),
        value: day
      })),
    []
  );

  const timeOptions = useMemo(
    () => [
      {
        label: i18n.t('campaignDashboard.labels.future'),
        value: 1
      },
      {
        label: i18n.t('campaignDashboard.labels.ago'),
        value: -1
      }
    ],
    []
  );

  const renderManageBidWeightModal = () => {
    const campaign = filteredList.find(
      (campaign) => campaign.campaignId === campaignToChangeBidWeight
    );
    if (!campaign) {
      return;
    }

    return (
      <BidWeightModal
        campaign={{
          id: campaign.campaignId,
          magnificationRatio: campaign.magnificationRatio
        }}
        onFinish={onCampaignBidWeightUpdated}
        onClose={hideManageBidWeightModal}
      />
    );
  };

  const renderManageAdminBidPriceModal = () => {
    const campaign = filteredList.find(
      (campaign) => campaign.campaignId === campaignToChangeAdminBidPrice
    );
    // 與按鈕的顯示條件用同一個判斷，避免資料重新載入後對不符合條件的活動開出彈窗。
    if (!campaign || !canManageAdminBidPrice(campaign)) {
      return;
    }

    return (
      <AdminBidPriceModal
        campaign={{
          id: campaign.campaignId,
          bidPrice: campaign.bidPrice,
          adminBidPrice: campaign.adminBidPrice
        }}
        onFinish={onCampaignAdminBidPriceUpdated}
        onClose={hideManageAdminBidPriceModal}
      />
    );
  };

  const renderRowBtns = useCallback(
    (campaignDashboardRecord: CampaignDashboardRecord) => {
      const campaignId: number = campaignDashboardRecord.campaignId;
      const editPath = `/orders/${campaignDashboardRecord.orderNumber}/campaign-groups/${campaignDashboardRecord.l1ObjectId}/campaigns/${campaignId}/edit`;

      const onClickBidWeightButton = () => showManageBidWeightModal(campaignId);
      const onClickAdminBidPriceButton = () =>
        showManageAdminBidPriceModal(campaignId);
      return [
        <PermissionChecker
          key='edit-btn'
          permissionAware={hasFuncs(Permission.CAMPAIGN_WRITE)
            .and(isPmax3Order(campaignDashboardRecord.orderId))
            .and(inCondition(() => !isPmp(campaignDashboardRecord)))}
        >
          <Link to={editPath}>
            <IconWithTooltip
              icon={faPencilAlt}
              tooltipProps={{
                id: `campaignListEditTip-${campaignId}`,
                tooltip: i18n.t('campaignList.labels.editHint')
              }}
            />
          </Link>
        </PermissionChecker>,
        <PermissionChecker
          key='report-btn'
          permissionAware={hasFuncs(Permission.REPORT_ADS)}
        >
          <Link to={getReportLink(campaignDashboardRecord)}>
            <IconWithTooltip
              icon={faChartArea}
              tooltipProps={{
                id: `campaignListReportTip-${campaignId}`,
                tooltip: i18n.t('campaignList.labels.reportHint')
              }}
            />
          </Link>
        </PermissionChecker>,
        <PermissionChecker
          key='bid-weight-btn'
          permissionAware={isRoles(RoleNames.sysAdmin)
            .and(inCondition(() => !isPmp(campaignDashboardRecord)))
            .or(
              isRoles(RoleNames.agencyAdmin)
                .and(hasTheseRoles(RoleNames.sysAdmin, RoleNames.agencyAdmin))
                .and(addOnEnabled(ADDONFEATURE.CAMPAIGN.BID_WEIGHT_SETTING))
                .and(inCondition(() => !isPmp(campaignDashboardRecord)))
            )}
        >
          <IconWithTooltip
            onClick={onClickBidWeightButton}
            icon={faBalanceScaleLeft}
            tooltipProps={{
              id: `campaignListBidWeightTip-${campaignId}`,
              tooltip: i18n.t('campaignList.labels.manageBidWeightModalTitle')
            }}
          />
        </PermissionChecker>,
        <PermissionChecker
          key='bid-price-btn'
          permissionAware={isRoles(RoleNames.sysAdmin)
            .and(
              inCondition(() => canManageAdminBidPrice(campaignDashboardRecord))
            )
            .or(
              isRoles(RoleNames.agencyAdmin)
                .and(hasTheseRoles(RoleNames.sysAdmin, RoleNames.agencyAdmin))
                .and(
                  addOnEnabled(ADDONFEATURE.CAMPAIGN.ADMIN_BID_PRICE_SETTING)
                )
                .and(
                  inCondition(() =>
                    canManageAdminBidPrice(campaignDashboardRecord)
                  )
                )
            )}
        >
          <IconWithTooltip
            onClick={onClickAdminBidPriceButton}
            icon={faDollarSign}
            tooltipProps={{
              id: `campaignListBidPriceTip-${campaignId}`,
              tooltip: i18n.t('campaignList.hints.manageAdminBidPrice')
            }}
          />
        </PermissionChecker>
      ];
    },
    [showManageBidWeightModal, showManageAdminBidPriceModal, isPmp]
  );

  return (
    <div className={styles.campaignDashboard}>
      {loading && <LoadingIndicator />}
      {campaignToChangeBidWeight && renderManageBidWeightModal()}
      {campaignToChangeAdminBidPrice && renderManageAdminBidPriceModal()}
      <div className={styles.title}>
        {i18n.t('campaignDashboard.titles.main')}
      </div>
      <div className={styles.titleBottomLine} />
      <div className={styles.summary}>
        <InfoDisplaySection
          data={yesterdaySummary}
          title={i18n.t('campaignDashboard.titles.yesterday')}
        />
        <InfoDisplaySection
          data={todaySummary}
          title={i18n.t('campaignDashboard.titles.today')}
        />
      </div>
      <div className={styles.campaignList}>
        <div className={styles.filterArea}>
          <div className={styles.flexArea}>
            <div className={styles.row}>
              <div className={styles.dayFilter}>
                <div>{i18n.t('campaignDashboard.labels.endsin')}</div>
                <Select
                  className={styles.daySelect}
                  options={dayOptions}
                  name='dayOptions'
                  simpleValue
                  value={days}
                  onChange={setDays}
                  maxMenuHeight={150}
                />
                <div>{i18n.t('campaignDashboard.labels.days')}</div>
                <Select
                  className={styles.timeOption}
                  options={timeOptions}
                  name='timeOptions'
                  simpleValue
                  value={timeOption}
                  onChange={setTimeOption}
                  maxMenuHeight={150}
                />
              </div>
              <div className={styles.filterWithTitle}>
                <div className={styles.filterTitle}>
                  {i18n.t('campaignDashboard.labels.adType')}
                </div>
                <Select
                  className={styles.filter}
                  options={adTypeOptions}
                  name='adTypeOptions'
                  isMulti
                  simpleValue
                  isClearable
                  value={selectedAdTypes}
                  onChange={setSelectedAdTypes}
                  maxMenuHeight={150}
                />
              </div>
              <div className={styles.filterWithTitle}>
                <div className={styles.filterTitle}>
                  {i18n.t('campaignDashboard.labels.campaignStatus')}
                </div>
                <Select
                  className={styles.filter}
                  options={campaignStatusOptions}
                  name='campaignStatusOptions'
                  isMulti
                  simpleValue
                  isClearable
                  value={selectedCampaignStates}
                  onChange={setSelectedCampaignStates}
                  maxMenuHeight={150}
                />
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.filterWithTitle}>
                <div className={styles.filterTitle}>
                  {i18n.t('campaignDashboard.labels.adjustmentNeeded')}
                </div>
                <Select
                  className={styles.filter}
                  options={adjustmentTagOptions}
                  name='adjustmentTagOptions'
                  isMulti
                  simpleValue
                  isClearable
                  value={selectedTagCodes}
                  onChange={setSelectedTagCodes}
                  maxMenuHeight={150}
                />
              </div>
              {searched && (
                <SearchBar
                  model={{
                    placeholder: i18n.t(
                      'campaignDashboard.placeholders.searchbar'
                    ),
                    search: setSearchString,
                    defaultValue: searchString
                  }}
                />
              )}
              <button
                className={`btn btn-primary btn-sm ${styles.searchButton}`}
                onClick={onSearchButtonClick}
              >
                {i18n.t('campaignDashboard.buttons.search')}
              </button>
            </div>
          </div>
        </div>
        {searched && (
          <div className={styles.downloadContainer}>
            <span onClick={download} className={styles.downloadWording}>
              <FontAwesomeIcon
                icon={faDownload}
                className={styles.downloadIcon}
              />
              {i18n.t('campaignDashboard.buttons.download')}
            </span>
          </div>
        )}
        <div className={styles.listContent}>
          <StickableTanstackTable
            stickFirstColumn
            stickLastColumn
            keyField={CampaignDashboardColumns.ID}
            data={filteredList}
            columns={columns}
            defaultSorted={[
              {
                dataField: CampaignDashboardColumns.ID,
                order: 'desc'
              }
            ]}
            noDataIndication={
              searched
                ? i18n.t('campaignDashboard.labels.noData')
                : i18n.t('campaignDashboard.labels.pleaseSearch')
            }
            renderRowBtns={renderRowBtns}
          />
        </div>
      </div>
    </div>
  );
};
